'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { Offer, OffersOffer } from '@/services/dataTypes'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref } from '@/constants/hooks'
import { apiOfferDetails } from '@/apis/offers'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import SimpleOfferModal from './SimpleOfferModal'
import DiscountTag from './DiscountTag'
import ExpiryTag from './ExpiryTag'
import { CheckCircle, Eye, Loader2, Copy, Check, ArrowRight, Tag } from 'lucide-react'
import { copyToClipboard } from '../utils/clipboard'

interface Props {
    product: OffersOffer; companyId: string; awaited_p_id?: string
    mer_slug_type: string; mer_slug: string; domain: string; ads_campaign: boolean
    onOfferClick?: () => void
}

const OffersListView = ({ product, companyId, awaited_p_id, mer_slug_type, mer_slug, domain, onOfferClick }: Props) => {
    const [p_data, setP_data] = useState<Offer | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [copied, setCopied] = useState(false)
    const merchantHref = getMerchantHref(product.merchant, mer_slug, mer_slug_type)

    useEffect(() => {
        if (!awaited_p_id || !companyId) return
        let cancelled = false
        apiOfferDetails(awaited_p_id, companyId).then(res => {
            if (!cancelled) { setP_data(res.data); setShowModal(true) }
        }).catch(() => { })
        return () => { cancelled = true }
    }, [awaited_p_id, companyId])

    const originalPrice = product?.offer?.original_price ? parseFloat(product.offer.original_price) : 0
    const salePrice = product?.offer?.sale_price ? parseFloat(product.offer.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer?.offer_title || product?.offer?.offer_detail, discountPercent)
    const isCoupon = !!product?.offer?.coupon_code
    const isProduct = product?.offer?.offer_type?.name === 'product'

    const handleViewDetail = async () => {
        if (loadingDetail) return
        setLoadingDetail(true)
        try {
            const res = await apiOfferDetails(product?.offer?.unique_id, companyId)
            setP_data(res?.data || product.offer); setShowModal(true)
        } catch { setP_data(product.offer); setShowModal(true) }
        finally { setLoadingDetail(false) }
    }

    return (
        <>
            {showModal && p_data && (
                <SimpleOfferModal data={p_data} onClose={() => setShowModal(false)} domain={domain}
                    merchantHref={merchantHref} finalDiscountTag={finalDiscountTag}
                    merchantImg={product.merchant?.merchant_logo} productImg={product?.offer?.product_image} />
            )}

            <div className="group relative bg-white rounded-2xl md:rounded-[1.5rem] border border-slate-200/80 hover:border-[#5C7A5C]/40 shadow-[0_2px_15px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_35px_rgba(45,62,45,0.12)] hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                    {/* Left/Top visual container */}
                    <div className="sm:w-44 flex-shrink-0 flex items-center justify-center p-5 bg-[#FAF7F2] border-b sm:border-b-0 sm:border-r border-slate-100 relative">
                        {isProduct && product?.offer?.product_image ? (
                            <div className="relative w-28 h-28 flex items-center justify-center">
                                <Image
                                    src={getBaseImageUrl(domain, product.offer.product_image, '')}
                                    alt={product.offer.offer_title || 'Product'}
                                    width={112}
                                    height={112}
                                    className="object-contain w-full h-full group-hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        ) : product.merchant?.merchant_logo ? (
                            <div className="relative w-24 h-24 flex items-center justify-center p-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
                                <Image
                                    src={getBaseImageUrl(domain, product.merchant.merchant_logo, '')}
                                    alt={product.merchant?.merchant_name || 'Brand'}
                                    width={96}
                                    height={96}
                                    className="object-contain w-full h-full group-hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        ) : (
                            <div className="w-16 h-16 rounded-2xl bg-[#E8F0E8] flex items-center justify-center text-[#5C7A5C] font-black text-xl">
                                {isCoupon ? '✂' : '🏷'}
                            </div>
                        )}

                        {finalDiscountTag && (
                            <div className="absolute top-2.5 right-2.5 sm:top-2.5 sm:left-2.5 sm:right-auto z-10">
                                <DiscountTag tag={finalDiscountTag} />
                            </div>
                        )}
                    </div>

                    {/* Right Content Area */}
                    <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
                        <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2.5">
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#E8F0E8] text-[#5C7A5C]">
                                    <CheckCircle size={10} /> Verified
                                </span>
                                <ExpiryTag endDate={product?.offer?.end_date} />
                                {isCoupon && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#F5C542]/15 text-[#D9A314] border border-[#F5C542]/30">
                                        <Tag size={9} /> Coupon Code
                                    </span>
                                )}
                            </div>

                            <h4 className="text-sm sm:text-base font-bold text-slate-800 mb-1.5 leading-snug line-clamp-2"
                                style={{ fontFamily: 'Georgia, serif' }}>
                                {discardHTMLTags(product?.offer?.offer_title?.replaceAll('_', ' '))}
                            </h4>

                            {product?.offer?.offer_detail && (
                                <p className="text-xs text-slate-500 line-clamp-2 mb-3 leading-relaxed">
                                    {discardHTMLTags(product.offer.offer_detail)}
                                </p>
                            )}

                            {isProduct && salePrice > 0 && (
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className="text-lg font-black text-[#2D3E2D]">
                                        {getCurrencySymbol(product.offer.currency)}{salePrice.toFixed(2)}
                                    </span>
                                    {originalPrice > 0 && (
                                        <span className="text-xs line-through text-slate-400">
                                            {getCurrencySymbol(product.offer.currency)}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {discountPercent && discountPercent > 0 && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E8F0E8] text-[#3D5A3D]">
                                            -{discountPercent}%
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Card Footer Actions */}
                        <div className="flex items-center gap-3 pt-3 border-t border-slate-100 mt-2">
                            <button
                                onClick={handleViewDetail}
                                disabled={loadingDetail}
                                className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold px-2.5 py-1 rounded-lg transition-all hover:bg-[#E8F0E8] text-[#5C7A5C] border border-[#5C7A5C]/20 disabled:opacity-40 cursor-pointer"
                            >
                                {loadingDetail ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
                                <span>Details</span>
                            </button>


                            <div className="ml-auto">
                                {onOfferClick ? (
                                    <button
                                        onClick={onOfferClick}
                                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-[#2D3E2D] shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                                        style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}
                                    >
                                        <span>{isCoupon ? 'Get Code' : isProduct ? 'Buy Now' : 'Get Deal'}</span>
                                        <ArrowRight size={12} />
                                    </button>
                                ) : (
                                    <OfferOutUrl
                                        unique_id={product.offer.unique_id}
                                        outUrl={product.offer.url}
                                        merchantHref={merchantHref}
                                        domain={domain}
                                    >
                                        <div
                                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-[#2D3E2D] shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                                            style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}
                                        >
                                            <span>{isCoupon ? 'Get Code' : isProduct ? 'Buy Now' : 'Get Deal'}</span>
                                            <ArrowRight size={12} />
                                        </div>
                                    </OfferOutUrl>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default OffersListView

